import React from 'react';
import './LegalPages.css';
import Footer from './Footer';

function PrivacyPolicy() {
  return (
    <div className="App">
      <div className="legal-page">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: January 13, 2026</p>
          
          <section>
            <h2>1. Introduction</h2>
            <p>
              Lilley Tech Syndicate LLC ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website (lilleytechsyndicate.com) or use our services (collectively, the "Services"). 
              This policy applies to all users worldwide, but additional rights may apply under local laws, such as 
              the General Data Protection Regulation (GDPR) in the European Union or the California Consumer Privacy 
              Act (CCPA/CPRA) in California.
            </p>
            <p>
              If you do not agree with the terms of this Privacy Policy, please do not access or use our Services.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <p>We collect information about you in the following ways:</p>
            <ul>
              <li><strong>Personal Data:</strong> Information you voluntarily provide, such as your name, email address, phone number, mailing address, or other contact details when you register, subscribe, contact us, or interact with our Services.</li>
              <li><strong>Usage Data:</strong> Automatically collected information about your interactions with our Services, including IP address, browser type and version, device type, operating system, referral sources, pages visited, time and date of visits, and clickstream data.</li>
              <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, pixels, and similar technologies to collect data on user activity. This may include preferences, session information, and analytics data.</li>
              <li><strong>Other Data:</strong> If applicable, payment information (processed via third-party providers), location data (if you enable it), or data from integrated third-party services.</li>
            </ul>
            <p>
              We do not collect sensitive personal data (e.g., health, financial, or biometric information) unless 
              explicitly required for a service and with your consent. Aggregated or anonymized data may be derived 
              from your information but is not considered personal data if it cannot identify you.
            </p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>To provide, maintain, and improve our Services, including personalizing your experience.</li>
              <li>To respond to your inquiries, provide customer support, and fulfill requests.</li>
              <li>To send you updates, newsletters, marketing communications, and promotional materials (only with your explicit consent; you can opt-out at any time).</li>
              <li>To monitor and analyze usage patterns, trends, and performance to enhance functionality.</li>
              <li>To detect, prevent, and address technical issues, fraud, or security threats.</li>
              <li>For internal business purposes, such as auditing, research, and compliance.</li>
            </ul>
            <p>
              We process personal data based on legitimate interests (e.g., service improvement), consent (e.g., marketing), 
              or contractual necessity (e.g., fulfilling services). We retain your data only as long as necessary for these 
              purposes or as required by law—typically up to 2 years after your last interaction for usage data, or longer 
              for legal obligations (e.g., tax records). After retention periods, data is securely deleted or anonymized.
            </p>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share your information only 
              in these limited circumstances:
            </p>
            <ul>
              <li><strong>Service Providers:</strong> With trusted third-party vendors who assist us in operating our Services, such as hosting providers (e.g., AWS), analytics tools (e.g., Google Analytics), email services (e.g., SendGrid), or payment processors. These providers are contractually obligated to protect your data and use it only for the services they provide to us.</li>
              <li><strong>Legal Requirements:</strong> When required by law, subpoena, court order, or government request, or to protect our rights, property, safety, or that of our users.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, reorganization, or sale of assets, where your data may be transferred to the new entity.</li>
              <li><strong>With Your Consent:</strong> For any other purpose with your explicit permission.</li>
            </ul>
            <p>
              If we transfer data internationally (e.g., to servers outside your jurisdiction), we ensure appropriate safeguards, 
              such as Standard Contractual Clauses under GDPR or equivalent measures, to maintain data protection levels.
            </p>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement reasonable technical, administrative, and organizational measures to protect your personal information, 
              including encryption (e.g., HTTPS for data in transit), access controls, firewalls, and regular security audits. 
              However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee 
              absolute security.
            </p>
            <p>
              In the event of a data breach involving personal information, we will notify affected users and relevant authorities 
              within 72 hours of discovery, as required by applicable laws (e.g., GDPR's 72-hour rule or CCPA requirements).
            </p>
          </section>

          <section>
            <h2>6. Your Rights</h2>
            <p>Depending on your location, you have certain rights regarding your personal data:</p>
            <ul>
              <li>Access and receive a copy of your data.</li>
              <li>Rectify inaccurate or incomplete information.</li>
              <li>Request deletion of your data (subject to legal exceptions).</li>
              <li>Object to or restrict processing of your data.</li>
              <li>Withdraw consent at any time (without affecting prior processing).</li>
              <li>Data portability: Receive your data in a structured, machine-readable format.</li>
              <li>For California residents under CCPA/CPRA: Opt-out of data "sales" (though we do not sell data), know categories of data collected/shared, and non-discrimination for exercising rights.</li>
            </ul>
            <p>
              To exercise these rights, contact us at the details in Section 11 or via our dedicated form at privacy@lilleytechsyndicate.com. 
              We will verify your identity and respond within 30 days (or 45 days for CCPA requests, extendable if needed). There is no 
              fee for reasonable requests, but we may charge for excessive or unfounded ones.
            </p>
            <p>
              If you are in the EU, you can also lodge a complaint with your local data protection authority.
            </p>
          </section>

          <section>
            <h2>7. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar technologies to enhance your experience, analyze usage, and deliver personalized content. 
              Categories include:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Necessary for core functionality (e.g., session management); cannot be disabled.</li>
              <li><strong>Analytics Cookies:</strong> Track usage for improvements (e.g., via Google Analytics); requires consent.</li>
              <li><strong>Marketing Cookies:</strong> For targeted ads or content; requires consent.</li>
            </ul>
            <p>
              You can manage preferences via our cookie consent banner on the website or disable cookies in your browser settings 
              (though this may limit functionality). We respect "Do Not Track" signals where applicable. For more details, see our 
              Cookie Policy [link to separate page if available].
            </p>
          </section>

          <section>
            <h2>8. Third-Party Links and Services</h2>
            <p>
              Our Services may contain links to third-party websites or integrate third-party tools (e.g., social media plugins). 
              We are not responsible for their privacy practices. We encourage you to review their policies before interacting. 
              Embedded content may collect data independently.
            </p>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>
              Our Services are not directed to individuals under the age of 18 (or 13 under COPPA). We do not knowingly collect 
              personal information from children. If we become aware of such collection, we will delete it promptly. Parents or 
              guardians can contact us for assistance.
            </p>
          </section>

          <section>
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will 
              notify you of material changes by posting the updated policy here, updating the "Last Updated" date, and, where 
              appropriate, emailing you if we have your contact information. Your continued use of the Services after changes 
              constitutes acceptance.
            </p>
          </section>

          <section>
            <h2>11. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
            </p>
            <p>
              <strong>Lilley Tech Syndicate LLC</strong><br />
              30 N Gould St Ste N<br />
              Sheridan, WY 82801 USA<br />
              Phone: (307) 204-5225<br />
              Email: privacy@lilleytechsyndicate.com (for privacy matters) or karl.lilley@gmail.com
            </p>
            <p>
              For data protection inquiries, our Data Protection Officer can be reached at the above email.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
